import type { ApplicationDetailCardProps } from '../types/components'
import { useState, type ReactNode } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EditIconButton } from '@/components/ui/EditIconButton'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import {
  formatApplicationId,
  formatApplicantId,
  getProductLongDescription,
} from '../utils/applicationFormatters'
import { formatAmount } from '@/lib/utils/formatters'
import { usePermission } from '@/hooks/usePermission'
import styles from '../styles/applicationDetail.module.css'
import { showSuccess, showError } from '@/lib/utils/notifications'
import {
  saveApplicantScreeningVerifiedIncome,
  underwritingCheckProcess,
} from '../services/applicationService'
import {
  shouldShowRiskRatingSectionAngular,
  isMaxRentBelowAppliedRent,
  shouldShowScreeningVerifiedIncomeWarning,
} from '../utils/applicationDetailCardVisibility'
import { ProductDetailDrawer } from './ProductDetailDrawer'

/** Angular `application-details.component.html` — Max Rent History `fa-info-circle` is role-gated. */
const MAX_RENT_HISTORY_ICON_ROLES = [
  'ROLE_ADMIN',
  'ROLE_UNDERWRITER',
  'ROLE_CUSTOMER_SERVICE_MANAGER',
] as const


export function ApplicationDetailCard({
  applicationAllData,
  applicationData,
  isCoresident,
  user,
  applicantStatusLabel,
  canCheckInOut,
  canAdjustRiskRating,
  onAdjustRiskRating,
  onOpenMaxRentHistory,
  onAfterUnderwritingCheck,
  onAfterScreeningIncomeSave,
}: ApplicationDetailCardProps) {
  const { hasPolicy } = usePermission()
  const [dafProductPanelOpen, setDafProductPanelOpen] = useState(false)
  const [isScreeningIncomeEdit, setIsScreeningIncomeEdit] = useState(false)
  const [screeningIncomeEditValue, setScreeningIncomeEditValue] = useState('')
  const [savingScreeningIncome, setSavingScreeningIncome] = useState(false)

  const property = applicationAllData.property as { allow_dynamic_access_fee?: boolean } | undefined
  /** Angular `application-details.component.ts`: `showRiskRating = property.allow_dynamic_access_fee ? true : !!risk_rating` */
  const showRiskRatingSection = shouldShowRiskRatingSectionAngular(applicationAllData)

  const riskDisplay =
    (applicationAllData.risk_rating as string | undefined) ||
    (applicationAllData.risk_rating_error as string | undefined) ||
    'Pending'

  /** Matches Angular: `(rent_added && balance_due)` → Not Paid In Full; numeric coercion for API strings. */
  const rentAdded = Boolean(applicationAllData.rent_added)
  const balanceDueRaw = applicationAllData.balance_due
  const hasOutstandingBalanceDue =
    balanceDueRaw != null &&
    balanceDueRaw !== '' &&
    !Number.isNaN(Number(balanceDueRaw)) &&
    Number(balanceDueRaw) > 0
  const accessFeeStatus =
    rentAdded && hasOutstandingBalanceDue
      ? 'Not Paid In Full'
      : rentAdded
        ? 'Confirmed'
        : 'Pending'

  const showDafProductSignal =
    Boolean(property?.allow_dynamic_access_fee) &&
    hasPolicy(['allow_dynamic_access_fee_signal_icon'])
  const productName =
    applicationAllData.product_name != null ? String(applicationAllData.product_name) : '—'
  const productTooltipDescription =
    applicationAllData.product_description != null ? String(applicationAllData.product_description) : ''
  const productDescription = getProductLongDescription(applicationAllData)

  const underwriterVerifiedIncome = Number(applicationData?.underwriter_verified_income_other ?? 0)
  // CS-1135 / Angular parity: warn only when unset — entered $0.00 clears the flag.
  const showScreeningIncomeWarning = shouldShowScreeningVerifiedIncomeWarning(
    applicationData?.underwriter_verified_income_other,
  )
  // Angular `calculateIncome()`: jobInfo.all_underwriter_verified_income is computed client-side
  // (the API does not return it) as the sum of each co-applicant's net `underwriter_verified_income_other`
  // when there is more than one co-resident.
  const coResidentEntries = Object.values(
    (applicationAllData?.co_resident_application as
      | Record<string, Record<string, unknown>>
      | undefined) ?? {},
  )
  const allUnderwriterVerifiedIncome =
    coResidentEntries.length > 1
      ? coResidentEntries.reduce((sum, coApplicant) => {
          const other = coApplicant?.underwriter_verified_income_other
          const type = coApplicant?.underwriter_verified_income_type
          return type === 'net' && other != null ? sum + Number(other) : sum
        }, 0)
      : 0
  const totalVerifiedIncome = isCoresident
    ? allUnderwriterVerifiedIncome
    : underwriterVerifiedIncome

  const itrValue = (applicationAllData?.itr_value as string) || 'N/A'
  const rentAmount = Number(applicationAllData?.rent_amount ?? 0)
  const totalItr = rentAmount > 0 ? (totalVerifiedIncome / rentAmount).toFixed(2) : '0'

  const maxRent = Number(applicationAllData?.max_rent ?? 0)
  // Angular: Max Rent info icon is red when max_rent < rent_amount, green when >= (UW warning).
  const maxRentBelowRent = isMaxRentBelowAppliedRent(
    applicationAllData?.max_rent,
    applicationAllData?.rent_amount,
  )
  const roleName = user?.current_role?.role_name
  const canEditScreeningVerifiedIncome = roleName === 'ROLE_UNDERWRITER'
  const showMaxRentHistoryControl =
    Boolean(applicationAllData?.max_rent_date) &&
    roleName != null &&
    (MAX_RENT_HISTORY_ICON_ROLES as readonly string[]).includes(roleName)

  const applicantPending = applicationData?.applicant_status === 'PENDING'
  const currentUserId = user?.user_id
  const underwritingBy = applicationData?.underwriting_by
  const underwritingWorkerName =
    applicationData?.userwriter_name != null ? String(applicationData.userwriter_name).trim() : ''
  const underwritingAt =
    applicationData?.underwriting_at != null ? String(applicationData.underwriting_at).trim() : ''
  const underwritingWorkingDetails = underwritingAt
    ? `${underwritingWorkerName} - ${underwritingAt}`
    : underwritingWorkerName
  const imCheckedOut =
    underwritingBy != null &&
    currentUserId != null &&
    String(underwritingBy) === String(currentUserId)
  const underwritingAction = imCheckedOut ? 'checkin' : 'checkout'
  const underwritingButtonLabel = imCheckedOut ? 'Check-In' : 'Check-Out'

  // Underwriting Check-In/Check-Out confirmation (replaces window.confirm with ConfirmDialog).
  const [underwritingConfirmOpen, setUnderwritingConfirmOpen] = useState(false)

  const handleUnderwritingClick = () => {
    setUnderwritingConfirmOpen(true)
  }

  const performUnderwritingAction = async () => {
    setUnderwritingConfirmOpen(false)
    const title = underwritingButtonLabel
    const applicantId = applicationData?.applicant_id
    if (
      applicantId == null ||
      (typeof applicantId !== 'string' && typeof applicantId !== 'number')
    ) {
      return
    }
    try {
      await underwritingCheckProcess(applicantId, underwritingAction)
      showSuccess(`${title} Added Successfully`)
      await onAfterUnderwritingCheck?.()
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : 'Request failed.')
    }
  }

  const startScreeningIncomeEdit = () => {
    const current = applicationData?.underwriter_verified_income_other
    setScreeningIncomeEditValue(current != null ? String(current) : '')
    setIsScreeningIncomeEdit(true)
  }

  const cancelScreeningIncomeEdit = () => {
    setIsScreeningIncomeEdit(false)
    setScreeningIncomeEditValue('')
  }

  const saveScreeningIncomeEdit = async () => {
    const numeric = parseFloat(String(screeningIncomeEditValue).replace(/[^0-9.]+/g, ''))
    if (!Number.isFinite(numeric) || numeric < 0) {
      showError('Please enter a valid income value.')
      return
    }

    const applicantId = applicationData?.applicant_id
    if (
      applicantId == null ||
      (typeof applicantId !== 'string' && typeof applicantId !== 'number')
    ) {
      showError('Applicant not found.')
      return
    }

    const currentType = applicationData?.underwriter_verified_income_type
    const incomeType =
      currentType != null && String(currentType).trim() !== ''
        ? String(currentType).trim()
        : 'net'

    setSavingScreeningIncome(true)
    try {
      await saveApplicantScreeningVerifiedIncome(applicantId, {
        underwriter_verified_income_other: numeric.toFixed(2),
        underwriter_verified_income_type: incomeType,
      })
      await onAfterScreeningIncomeSave?.()
      showSuccess('Verified income Updated Successfully')
      cancelScreeningIncomeEdit()
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : 'Failed to update verified income')
    } finally {
      setSavingScreeningIncome(false)
    }
  }

  return (
    <section className={styles.applicationDetailCard} data-testid="application-detail-card">
      <div className={styles.applicationSummaryGrid}>
        <ApplicationSummaryBlock>
          {showRiskRatingSection ? (
            <ApplicationSummaryRow label="Application Risk Rating">
              {riskDisplay}
              {canAdjustRiskRating && onAdjustRiskRating ? (
                <EditIconButton
                  className="ml-2 h-5 w-5 rounded p-0"
                  iconClassName="h-3.5 w-3.5"
                  data-testid="application-detail-card-risk-rating-adjust-button"
                  onClick={onAdjustRiskRating}
                  title="Adjust risk rating"
                  aria-label="Adjust risk rating"
                />
              ) : null}
            </ApplicationSummaryRow>
          ) : null}
          <ApplicationSummaryRow label="Application ID">
            {applicationAllData?.application_id != null
              ? formatApplicationId(Number(applicationAllData.application_id))
              : 'N/A'}
            {showDafProductSignal ? (
              <SectionInfoIcon
                align="start"
                maxWidth={360}
                iconSize={16}
                aria-label="Product details"
                data-testid="application-detail-card-daf-product-info-button"
                onClick={() => setDafProductPanelOpen(true)}
                clampPanelHeight
                contentClassName="px-3 py-2 text-xs leading-relaxed text-slate-700"
                className={`${styles.applicationIdInfoIcon} ml-1.5 align-middle`}
                content={
                  <>
                    <p className="mb-1.5 font-semibold text-slate-800">{productName}</p>
                    {productTooltipDescription.trim() ? (
                      <p className="m-0 whitespace-pre-wrap">{productTooltipDescription}</p>
                    ) : (
                      <p className="m-0 whitespace-pre-wrap">No description.</p>
                    )}
                  </>
                }
              />
            ) : null}
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Applicant ID">
            {applicationData?.applicant_id != null
              ? formatApplicantId(Number(applicationData.applicant_id))
              : 'N/A'}
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Applicant Status">
            {applicantStatusLabel}
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Progress">
            {applicationData?.progress_status != null ? String(applicationData.progress_status) : 'N/A'}
          </ApplicationSummaryRow>
        </ApplicationSummaryBlock>

        <ApplicationSummaryBlock>
          <ApplicationSummaryRow label="Access Fee Payment">
            {accessFeeStatus}
            <span
              className={`${styles.statusIndicator} ${
                accessFeeStatus === 'Confirmed' ? styles.green : styles.red
              }`}
            />
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Screening Verified Income">
            {isScreeningIncomeEdit && canEditScreeningVerifiedIncome ? (
              <span className={styles.summaryInlineEdit}>
                <span>$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className={styles.summaryInlineInput}
                  value={screeningIncomeEditValue}
                  placeholder="0.00"
                  data-testid="application-detail-card-screening-income-input"
                  onChange={(event) => setScreeningIncomeEditValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      void saveScreeningIncomeEdit()
                    }
                    if (event.key === 'Escape') {
                      cancelScreeningIncomeEdit()
                    }
                  }}
                  disabled={savingScreeningIncome}
                />
                <button
                  type="button"
                  className={styles.summaryIconButtonSuccess}
                  title="Save"
                  aria-label="Save screening verified income"
                  data-testid="application-detail-card-save-screening-income-button"
                  disabled={savingScreeningIncome}
                  onClick={() => void saveScreeningIncomeEdit()}
                >
                  <i className="fa fa-check" aria-hidden />
                </button>
                <button
                  type="button"
                  className={styles.summaryIconButtonDanger}
                  title="Cancel"
                  aria-label="Cancel screening verified income edit"
                  data-testid="application-detail-card-cancel-screening-income-button"
                  disabled={savingScreeningIncome}
                  onClick={cancelScreeningIncomeEdit}
                >
                  <i className="fa fa-times" aria-hidden />
                </button>
              </span>
            ) : (
              <>
                {formatAmount(underwriterVerifiedIncome)}
                {showScreeningIncomeWarning ? (
                  <span className={styles.inlineSummaryIcon} role="img" aria-label="Warning">
                    ⚠️
                  </span>
                ) : null}
                {canEditScreeningVerifiedIncome ? (
                  <EditIconButton
                    className={styles.summaryEditButton}
                    title="Edit Verified Income"
                    aria-label="Edit screening verified income"
                    data-testid="application-detail-card-edit-screening-income-button"
                    onClick={startScreeningIncomeEdit}
                  />
                ) : null}
              </>
            )}
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Total Verified Income">
            {formatAmount(totalVerifiedIncome)}
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="ITR Threshold">
            {itrValue}
            {applicationAllData?.pmc_itr === 1 ? (
              <span className={styles.inlineSummaryIcon}>⚠️</span>
            ) : null}
            {applicationAllData?.pmc_itr === 0 ? (
              <span className={styles.inlineSummaryIcon}>🌍</span>
            ) : null}
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Last Updated">
            {applicationAllData?.itr_update_date ? String(applicationAllData.itr_update_date) : '-'}
          </ApplicationSummaryRow>
        </ApplicationSummaryBlock>

        <ApplicationSummaryBlock>
          <ApplicationSummaryRow label="Total ITR">{totalItr}</ApplicationSummaryRow>
          <ApplicationSummaryRow label="Max Rent">
            <span className="inline-flex items-center align-middle">
              <span>{formatAmount(maxRent)}</span>
              {showMaxRentHistoryControl ? (
                <SectionInfoIcon
                  align="start"
                  maxWidth={180}
                  iconSize={16}
                  aria-label="Max rent history"
                  data-testid="application-detail-card-max-rent-history-button"
                  onClick={() => onOpenMaxRentHistory?.()}
                  clampPanelHeight={false}
                  contentClassName="px-3 py-2 text-xs font-medium text-slate-800"
                  className={`${maxRentBelowRent ? styles.maxRentHistoryIcon : styles.maxRentHistoryIconOk} ml-2 h-4 w-4 shrink-0`}
                  content="Max Rent History"
                />
              ) : null}
            </span>
          </ApplicationSummaryRow>
          <ApplicationSummaryRow label="Last Updated">
            {applicationAllData?.max_rent_date ? String(applicationAllData.max_rent_date) : '-'}
          </ApplicationSummaryRow>
          {applicantPending && underwritingWorkerName ? (
            <ApplicationSummaryRow label="Working">
              {underwritingWorkingDetails}
            </ApplicationSummaryRow>
          ) : null}
          {applicantPending && canCheckInOut ? (
            <button
              type="button"
              className={styles.checkInButton}
              data-testid={`application-detail-card-underwriting-${underwritingAction}-button`}
              onClick={() => void handleUnderwritingClick()}
            >
              {underwritingButtonLabel}
            </button>
          ) : null}
        </ApplicationSummaryBlock>

      </div>

      {dafProductPanelOpen && showDafProductSignal ? (
        <ProductDetailDrawer
          productName={productName}
          productDescription={productDescription}
          onClose={() => setDafProductPanelOpen(false)}
          testId="application-detail-card-product-panel"
          titleTestId="application-detail-card-product-panel-title"
          closeButtonTestId="application-detail-card-product-panel-close-button"
        />
      ) : null}

      <ConfirmDialog
        isOpen={underwritingConfirmOpen}
        title={underwritingButtonLabel}
        message={`${underwritingButtonLabel} to application`}
        primaryLabel="Yes"
        secondaryLabel="No"
        onPrimary={() => void performUnderwritingAction()}
        onSecondary={() => setUnderwritingConfirmOpen(false)}
        overlayDataTestId="application-detail-card-confirm-underwriting"
        primaryButtonDataTestId="application-detail-card-confirm-underwriting-primary-button"
        secondaryButtonDataTestId="application-detail-card-confirm-underwriting-secondary-button"
        closeButtonDataTestId="application-detail-card-confirm-underwriting-close-button"
      />
    </section>
  )
}

function ApplicationSummaryBlock({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={`${styles.applicationSummaryBlock} ${className}`}>{children}</div>
}

function ApplicationSummaryRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <p className={styles.applicationSummaryRow}>
      <strong>{label}: </strong>
      <span>{children || '-'}</span>
    </p>
  )
}
